<script setup lang="ts">
import { ref, computed, onMounted, watch } from "vue";
import { useI18n } from "vue-i18n";
import { ExclamationIcon, ChatAlt2Icon } from "@heroicons/vue/outline";
import type { ShortDocument } from "@kekkle/shared/types/documents/short-document/short-document";
import TinyUserThumb from "@/components/TinyUserThumb.vue";
import { useShortsStore } from "@/stores/modules/shorts/shorts";
import { useGroupsStore } from "@/stores/modules/groups/groups";
import { useAuthStore } from "@/stores/modules/auth/auth";
import { dateTimeAgo } from "@/helpers/dates/date-time-ago";
import {
  getBlurhashUrlSync,
  requestBlurhashUrl,
} from "@/helpers/blurhash/blurhash-url-queue";
import { DEFAULT_SHORT_MAX_DURATION_SECONDS } from "@/constants/shorts";

type Props = {
  short: ShortDocument;
  groupId: string;
};

const props = defineProps<Props>();
const emit = defineEmits<{ (e: "click", short: ShortDocument): void }>();

const { t } = useI18n();
const shortsStore = useShortsStore();
const groupsStore = useGroupsStore();
const authStore = useAuthStore();

const localThumbnailUrl = ref<string>("");
const hashUrl = ref<string>("");

const isReported = computed(() => (props.short.reported_by?.length ?? 0) > 0);
const isRedacted = computed(() => Boolean(props.short.redacted));

const storeThumbnail = computed(
  () => shortsStore.thumbnailsById[props.short._id]?.url,
);
const thumbnailUrl = computed(
  () => storeThumbnail.value || localThumbnailUrl.value,
);

const isUnviewed = computed(() => !shortsStore.isViewed(props.short));

const authorName = computed(() => {
  if (props.short.created_by === authStore.authUuid) {
    return t("pages.shorts.you");
  }
  const member = groupsStore.groupData?.members?.[props.short.created_by];
  if (member?.name) {
    return member.name.split(" ")[0] || "";
  }
  return t("global.member");
});

const timeAgo = computed(() => {
  const date = props.short.created_at || props.short.timestamp;
  return dateTimeAgo(new Date(date));
});

const formattedDuration = computed(() => {
  const sec = props.short.duration || DEFAULT_SHORT_MAX_DURATION_SECONDS;
  const s = Math.round(sec);
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return `${m}:${rem < 10 ? "0" : ""}${rem}`;
});

const commentCount = computed(() => props.short.number_of_comments ?? 0);

async function loadMedia(): Promise<void> {
  if (isReported.value || isRedacted.value) return;

  if (props.short.blurhash && !hashUrl.value) {
    const sync = getBlurhashUrlSync(props.short.blurhash);
    if (sync) {
      hashUrl.value = sync;
    } else {
      requestBlurhashUrl(props.short.blurhash, (url) => {
        hashUrl.value = url;
      });
    }
  }

  if (!storeThumbnail.value) {
    const url = await shortsStore.resolveThumbnailUrl(props.short);
    if (url) {
      localThumbnailUrl.value = url;
    }
  }
}

onMounted(() => {
  loadMedia();
});

watch(
  () => [props.short._id, props.short.redacted, isReported.value],
  () => {
    loadMedia();
  },
);
</script>

<template>
  <div
    class="group relative aspect-9/16 w-full cursor-pointer overflow-hidden rounded-2xl border-2 border-black-dark bg-black-dark shadow-dark-hard-sm transition-all duration-200 select-none hover:-translate-y-0.5 hover:shadow-none active:translate-y-0"
    :class="isUnviewed ? 'ring-2 ring-kekkle-yellow' : ''"
    @click="emit('click', short)"
  >
    <!-- Reported or Redacted State -->
    <div
      v-if="isReported || isRedacted"
      class="flex h-full w-full flex-col items-center justify-center bg-yellow-100 p-3 text-center dark:bg-yellow-950/40"
    >
      <ExclamationIcon class="h-8 w-8 text-yellow-600 dark:text-yellow-400" />
      <span class="mt-2 text-xs font-bold text-yellow-800 dark:text-yellow-300">
        {{ $t("components.report.reportTitle") }}
      </span>
      <span
        v-if="isRedacted"
        class="mt-1 line-clamp-3 px-1 text-[10px] leading-tight font-medium text-yellow-800 dark:text-yellow-300"
      >
        {{ short.caption || "This short was removed by content moderation." }}
      </span>
    </div>

    <!-- Media Poster -->
    <template v-else>
      <img
        v-if="thumbnailUrl"
        :src="thumbnailUrl"
        alt="Short thumbnail"
        class="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
        loading="lazy"
      />
      <img
        v-else-if="hashUrl"
        :src="hashUrl"
        alt="Short preview"
        class="h-full w-full object-cover"
      />
      <div
        v-else
        class="flex h-full w-full items-center justify-center bg-linear-to-br from-gray-900 to-gray-800"
      >
        <span class="text-3xl opacity-50">🎬</span>
      </div>

      <!-- Play Hover Icon -->
      <div
        v-if="!isRedacted"
        class="pointer-events-none absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-200 group-hover:opacity-100"
      >
        <div
          class="flex h-12 w-12 items-center justify-center rounded-full border-2 border-black-dark bg-kekkle-yellow text-xl text-black-dark shadow-md"
        >
          ▶
        </div>
      </div>

      <!-- Top Badges Row -->
      <div
        class="absolute top-2 right-2 left-2 z-10 flex items-center justify-between"
      >
        <!-- Unviewed / New Badge or Redacted Badge -->
        <span
          v-if="isUnviewed && !isRedacted"
          class="rounded-full border border-black-dark bg-kekkle-yellow px-2 py-0.5 text-[10px] font-black text-black-dark uppercase shadow-sm"
        >
          {{ $t("pages.shorts.unviewed") }}
        </span>
        <span
          v-else-if="isRedacted"
          class="rounded-full border border-black-dark bg-kekkle-yellow px-2 py-0.5 text-[10px] font-black text-black-dark uppercase shadow-sm"
        >
          {{ $t("components.report.reportTitle") }}
        </span>
        <span v-else />

        <!-- Duration Pill -->
        <span
          v-if="!isRedacted"
          class="rounded-full border border-white/20 bg-black-dark/60 px-2 py-0.5 text-[11px] font-bold text-white backdrop-blur-xs"
        >
          {{ formattedDuration }}
        </span>
      </div>

      <!-- Bottom Gradient Overlay -->
      <div
        class="pointer-events-none absolute inset-x-0 bottom-0 z-10 flex flex-col justify-end bg-linear-to-t from-black-dark/90 via-black-dark/50 to-transparent p-3 pt-10 text-left"
      >
        <!-- Author Row -->
        <div class="flex items-center gap-1.5">
          <div class="short-avatar h-5 w-5 shrink-0">
            <TinyUserThumb
              :uuid="short.created_by"
              class="h-full w-full rounded-full border border-white/80 object-cover"
            />
          </div>
          <span class="truncate text-xs font-bold text-white drop-shadow-sm">
            {{ authorName }}
          </span>
          <span class="text-[10px] text-gray-300 drop-shadow-sm">
            • {{ timeAgo }}
          </span>
        </div>

        <!-- Caption -->
        <p
          v-if="short.caption"
          class="mt-1 line-clamp-2 text-xs font-medium text-white/95 drop-shadow-sm"
        >
          {{ short.caption }}
        </p>

        <!-- Stats / Comments Row -->
        <div
          v-if="commentCount > 0"
          class="mt-1.5 flex items-center gap-1 text-[11px] font-semibold text-gray-200"
        >
          <ChatAlt2Icon class="h-3.5 w-3.5 text-kekkle-yellow" />
          <span>{{ commentCount }}</span>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.short-avatar {
  width: 20px;
  height: 20px;
}

.short-avatar :deep(div) {
  width: 20px !important;
  height: 20px !important;
}

.short-avatar :deep(img) {
  width: 20px !important;
  height: 20px !important;
  object-fit: cover !important;
}
</style>
